import React, { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/counter.css";
import Seo, { buildProjectJsonLd } from "../components/Seo";
import Reveal from "../components/Reveal";
import RevealImage from "../components/RevealImage";
import { getProject, projects } from "../data/projects";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = getProject(slug);
  const [index, setIndex] = useState(-1);

  const slides = useMemo(
    () =>
      project?.images.map((img) => ({
        src: img.full,
        description: img.caption,
      })) || [],
    [project]
  );

  if (!project) return <Navigate to="/works" replace />;

  const nextIdx = projects.findIndex((p) => p.slug === project.slug) + 1;
  const next = projects[nextIdx] || projects[0];

  return (
    <>
      <Seo
        title={project.title}
        description={project.summary}
        image={`/images/${project.slug}/full/${project.heroId}.jpg`}
        type="article"
        jsonLd={buildProjectJsonLd(project)}
      />

      {/* HERO */}
      <section className="relative h-[100svh] min-h-[560px] -mt-16 md:-mt-20 w-full overflow-hidden">
        <motion.img
          src={`/images/${project.slug}/full/${project.heroId}.jpg`}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.33, 1, 0.68, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/10 to-ink/70" />
        <div className="relative z-10 h-full container-page flex flex-col justify-end pb-16 md:pb-24 text-paper">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.33, 1, 0.68, 1] }}
          >
            <div className="text-[11px] tracking-widest3 uppercase text-paper/80 mb-5">
              {project.typology} — {project.location}
            </div>
            <h1 className="font-display text-[2.5rem] sm:text-6xl md:text-8xl lg:text-9xl leading-[0.95] tracking-wider sm:tracking-widest2 break-words">
              {project.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="container-page py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <div className="eyebrow mb-4">Overview</div>
            <dl className="text-sm space-y-4">
              <div>
                <dt className="eyebrow">Typology</dt>
                <dd className="mt-1 font-display text-lg tracking-wide">{project.typology}</dd>
              </div>
              <div>
                <dt className="eyebrow">Location</dt>
                <dd className="mt-1 font-display text-lg tracking-wide">{project.location}</dd>
              </div>
              {project.year && (
                <div>
                  <dt className="eyebrow">Year</dt>
                  <dd className="mt-1 font-display text-lg tracking-wide">{project.year}</dd>
                </div>
              )}
            </dl>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.1}>
            <p className="font-display text-2xl md:text-4xl leading-snug tracking-wide">
              {project.summary}
            </p>
          </Reveal>
        </div>
      </section>

      {/* GALLERY */}
      <section className="container-page pb-24 md:pb-40">
        <Reveal>
          <div className="eyebrow mb-10">Gallery — {project.images.length} images</div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {project.images.map((img, i) => {
            // Varied layout: every few images change column span for editorial rhythm
            const pattern = i % 6;
            let span = "md:col-span-12";
            let aspect = "aspect-[16/10]";
            if (pattern === 1) {
              span = "md:col-span-7";
              aspect = "aspect-[4/5]";
            } else if (pattern === 2) {
              span = "md:col-span-5 md:mt-16";
              aspect = "aspect-[4/5]";
            } else if (pattern === 3) {
              span = "md:col-span-6";
              aspect = "aspect-[3/4]";
            } else if (pattern === 4) {
              span = "md:col-span-6 md:mt-24";
              aspect = "aspect-[3/4]";
            } else if (pattern === 5) {
              span = "md:col-span-8 md:col-start-3";
              aspect = "aspect-[16/9]";
            }
            return (
              <Reveal key={img.id} className={span} delay={Math.min(i * 0.04, 0.3)}>
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  className="group block w-full text-left"
                  aria-label={`Open image ${i + 1}`}
                >
                  <RevealImage aspect={aspect}>
                    <img
                      src={img.full}
                      alt={img.caption || project.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-smooth group-hover:scale-[1.03]"
                    />
                    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors duration-500" />
                  </RevealImage>
                  {img.caption && (
                    <p className="mt-3 text-xs md:text-sm text-smoke max-w-2xl leading-relaxed">
                      <span className="font-display tracking-widest2 text-ink mr-3">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {img.caption}
                    </p>
                  )}
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* NEXT PROJECT */}
      <section className="border-t border-mist">
        <Link to={`/works/${next.slug}`} className="group block">
          <div className="container-page py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-4">
              <div className="eyebrow mb-3">Next project</div>
              <div className="font-display text-3xl sm:text-4xl md:text-6xl tracking-normal sm:tracking-widest2 group-hover:opacity-60 transition-opacity">
                {next.title}
              </div>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <RevealImage aspect="aspect-[16/9]" direction="right">
                <img
                  src={`/images/${next.slug}/full/${next.cover}.jpg`}
                  alt={next.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-smooth group-hover:scale-[1.04]"
                />
              </RevealImage>
            </div>
            <div className="md:col-span-1 text-right">
              <span className="inline-block font-display text-3xl group-hover:translate-x-2 transition-transform">
                →
              </span>
            </div>
          </div>
        </Link>
      </section>

      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={slides}
        plugins={[Captions, Counter]}
        styles={{
          container: { backgroundColor: "rgba(10,10,10,0.96)" },
        }}
        carousel={{ finite: false }}
      />
    </>
  );
}
